import { Button, LoadingState, MonoLabel } from '@/component';
import { useBillingActions, useBillingModals, useBillingSummary, useReceipts } from '@/hook';
import { cn } from '@/lib/cn';
import { formatUsd } from '@/lib/money';
import { formatReceiptDate } from '@/lib/time';
import { tierCards, type TierAction, type TierCardModel } from './tierCards';

const LOOK = {
  loud: 'border-0 bg-accFill text-accOn hover:opacity-90',
  quiet: 'border border-line2 bg-transparent text-ink hover:text-acc',
  muted: 'border border-line2 bg-transparent text-mute hover:text-acc',
} as const;

/** Settings, Plan & billing: the two tier cards, the placeholder warning (or
 * the closed notice), the payment method and the receipts. */
export function PlanTab() {
  const summary = useBillingSummary();
  const receipts = useReceipts();
  const { openPlan, openCheckout } = useBillingModals();
  const { keep, openPortal, pending, error } = useBillingActions();

  if (!summary) return <LoadingState />;

  function act(action: TierAction) {
    if (action.type === 'checkout') openCheckout(action.variant);
    else if (action.type === 'plan') openPlan();
    else if (action.type === 'keep') void keep();
  }

  return (
    <div className="mt-10">
      <div className="grid max-w-[620px] grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-[18px]">
        {tierCards(summary).map((card) => (
          <TierCard key={card.key} card={card} disabled={pending} onAct={act} />
        ))}
      </div>
      {summary.checkout.open ? (
        <MonoLabel tone="down" className="mt-[18px] block">
          Placeholder pricing and limits
        </MonoLabel>
      ) : (
        <MonoLabel tone="mute" className="mt-[18px] block">
          Plans open soon
        </MonoLabel>
      )}
      {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
      <div className="mt-[52px] grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] items-start gap-[52px]">
        <section>
          <h2 className="border-b border-line pb-3 text-[15px] font-semibold">Payment method</h2>
          {summary.paymentMethod ? (
            <div className="flex items-center gap-4 border-b border-stripeA py-5">
              <div className="h-[30px] w-[46px] shrink-0 rounded border border-line" aria-hidden="true" />
              <div className="flex-1">
                <div className="font-mono text-sm">•••• {summary.paymentMethod.last4}</div>
                <div className="mt-[2px] text-[13px] text-mute">
                  Expires {String(summary.paymentMethod.expMonth).padStart(2, '0')} / {String(summary.paymentMethod.expYear).slice(-2)}
                </div>
              </div>
              <Button variant="outline" size="sm" className="px-[13px] py-2" onClick={() => void openPortal()}>
                Update
              </Button>
            </div>
          ) : (
            <p className="py-5 text-sm text-mute">No card on file. One is added at your first checkout.</p>
          )}
        </section>
        <section id="receipts">
          <h2 className="border-b border-line pb-3 text-[15px] font-semibold">Receipts</h2>
          {receipts === undefined ? (
            <LoadingState />
          ) : receipts.length === 0 ? (
            <p className="py-5 text-sm text-mute">Nothing paid yet.</p>
          ) : (
            receipts.map((receipt) => (
              <div key={receipt.key} className="flex items-center justify-between gap-4 border-b border-stripeA py-[14px]">
                <span className="font-mono text-[13px] text-mute">{formatReceiptDate(receipt.at)}</span>
                <span className="flex-1 text-sm">{receipt.label}</span>
                <span className="font-mono text-[13px] tabular-nums">{formatUsd(receipt.amountCents)}</span>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

function TierCard({ card, disabled, onAct }: { card: TierCardModel; disabled: boolean; onAct: (action: TierAction) => void }) {
  const { cta } = card;
  return (
    <div
      data-current={card.current || undefined}
      className={cn('flex flex-col rounded-lg border bg-surface px-[22px] py-6', card.current ? 'border-accFill' : 'border-line')}
    >
      <div className="flex min-h-[34px] flex-col gap-[5px]">
        <MonoLabel tone={card.tint} className="tracking-[0.18em]">
          {card.name}
        </MonoLabel>
        <MonoLabel className="whitespace-nowrap text-[9px]">{card.badge}</MonoLabel>
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-[34px] font-semibold tracking-[-0.035em]">{card.price}</span>
        <span className="text-[13px] text-mute">{card.per}</span>
      </div>
      <div className="mt-5">
        {card.lines.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 border-t border-stripeA py-[11px]">
            <span className="text-[13px] text-mute">{k}</span>
            <span className="text-right text-[13px] font-semibold">{v}</span>
          </div>
        ))}
      </div>
      {cta && (
        <button
          type="button"
          disabled={disabled || cta.action.type === 'none'}
          aria-label={`${cta.label}: ${card.name}`}
          onClick={() => onAct(cta.action)}
          className={cn(
            'mt-[22px] whitespace-nowrap rounded-md px-[14px] py-[11px] text-[13px] font-semibold disabled:pointer-events-none',
            cta.action.type === 'none' ? '' : 'disabled:opacity-50',
            LOOK[cta.look]
          )}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
